// DOCK hexagonal — reemplaza al sidebar (BeeNavbar, retirado). SIN contenedor
// glass: en reposo es un "pequeño panal" (el hexágono de la sección ACTIVA + medio
// hexágono asomado detrás) anclado en la ESQUINA INFERIOR IZQUIERDA, sin fondo. Al
// acercar el mouse (hover), enfocar con teclado (focus-within) o abrir un popover
// (aria-expanded) se DESPLIEGA la FILA de hexágonos HACIA LA DERECHA con stagger;
// al salir se repliega tras ~300ms (delay en CSS). En táctil, el primer tap
// despliega y un tap fuera repliega (React).
// Orden: home · notificaciones (campana anclada al dock) · favoritos · y las
// secciones según acceso (mis tareas, chat, células, directorio, papelera, admin).
// Estilos en chrome.css. Montado por RequireAuth en TODA vista protegida.
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useAuth } from '../../auth/AuthContext';
import type { PermKey, Project, UserPrefs } from '../../lib/types';
import { NotifBell } from '../notifs/NotifBell';
import './chrome.css';

// Íconos inline (trazo 1.7, heredan currentColor) — mismos que usaba el sidebar
// más un home y una estrella para las secciones nuevas del dock.
const ICONS: Record<string, ReactNode> = {
  home: (
    <svg viewBox="0 0 24 24">
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v10h14V10" />
      <path d="M9.5 20v-6h5v6" />
    </svg>
  ),
  favoritos: (
    <svg viewBox="0 0 24 24">
      <path d="M12 3.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9z" />
    </svg>
  ),
  tareas: (
    <svg viewBox="0 0 24 24">
      <path d="M9 6h12M9 12h12M9 18h12" />
      <path d="M3.5 6l1.2 1.2L7 4.9" />
      <path d="M3.5 12l1.2 1.2L7 10.9" />
      <path d="M3.5 18l1.2 1.2L7 16.9" />
    </svg>
  ),
  // Panorama: eje de tiempo con barras escalonadas (la lectura gantt de la página).
  panorama: (
    <svg viewBox="0 0 24 24">
      <path d="M3.2 3.5v17h17.6" />
      <path d="M6.5 7h8" />
      <path d="M9.5 11.5h9" />
      <path d="M6.5 16h5.5" />
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24">
      <path d="M21 12a8 8 0 0 1-8 8H4l2-3.2A8 8 0 1 1 21 12z" />
    </svg>
  ),
  celulas: (
    <svg viewBox="0 0 24 24">
      <path d="M8.5 3.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
      <path d="M19.5 3.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
      <path d="M14 13.5h-4l-2 3.5 2 3.5h4l2-3.5z" />
    </svg>
  ),
  directorio: (
    <svg viewBox="0 0 24 24">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      <path d="M16.5 5.5a3.2 3.2 0 0 1 0 5.4" />
      <path d="M17.5 14.8c1.9.7 3 2.3 3 4.7" />
    </svg>
  ),
  papelera: (
    <svg viewBox="0 0 24 24">
      <path d="M4 7h16" />
      <path d="M9 7V4.5h6V7" />
      <path d="M6 7l1 13.5h10L18 7" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2.8v3M12 18.2v3M2.8 12h3M18.2 12h3M5.5 5.5l2.1 2.1M16.4 16.4l2.1 2.1M18.5 5.5l-2.1 2.1M7.6 16.4l-2.1 2.1" />
    </svg>
  ),
};

// Secciones de navegación con ruta. Notificaciones y favoritos NO son NavLinks:
// abren popover y se intercalan aparte (home · campana · favoritos · secciones).
type NavItem = { key: string; to: string; label: string; end?: boolean };
const HOME: NavItem = { key: 'home', to: '/', label: 'Inicio', end: true };
const SECTIONS: NavItem[] = [
  { key: 'tareas', to: '/mis-tareas', label: 'Mis tareas' },
  { key: 'panorama', to: '/panorama', label: 'Panorama' },
  { key: 'chat', to: '/chat', label: 'Chat' },
  { key: 'celulas', to: '/celulas', label: 'Células' },
  { key: 'directorio', to: '/directorio', label: 'Directorio' },
  { key: 'papelera', to: '/papelera', label: 'Papelera' },
  { key: 'admin', to: '/admin', label: 'Admin' },
];

// Un hexágono-enlace de sección (NavLink → estado activo por ruta actual).
// `index` alimenta el stagger del despliegue (--i); `lead` marca el hexágono que
// queda visible en reposo (el "panal" pequeño) cuando el dock está replegado.
function DockLink({ item, index, lead }: { item: NavItem; index: number; lead?: boolean }) {
  return (
    <div className={lead ? 'bd-slot is-lead' : 'bd-slot'} style={{ '--i': index } as CSSProperties}>
      <NavLink
        to={item.to}
        end={item.end}
        className={({ isActive }) => (isActive ? 'bee-dock-hex active' : 'bee-dock-hex')}
        aria-label={item.label}
      >
        <span className="bd-ico">{ICONS[item.key]}</span>
      </NavLink>
    </div>
  );
}

// Hexágono de FAVORITOS: popover hacia la derecha con los proyectos favoritos del
// usuario (userPrefs.favorites) resueltos a nombre por el cache de qk.projects.
function DockFavorites({ index }: { index: number }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Prefs personales (misma llave/forma que dashboard: bajo qk.prefs se cachea el
  // UserPrefs desenvuelto). Solo se pide al abrir el popover.
  const { data: prefs } = useQuery({
    queryKey: qk.prefs,
    queryFn: () => apiGet<{ prefs: UserPrefs }>('/api/prefs').then((r) => r.prefs),
    enabled: open,
  });
  // Nombres de proyectos: mismo cache compartido con dashboard/chat/búsqueda
  // (qk.projects('mine') guarda el ARRAY desenvuelto).
  const { data: projects } = useQuery({
    queryKey: qk.projects('mine'),
    queryFn: () => apiGet<{ projects: Project[] }>('/api/projects?filter=mine').then((r) => r.projects),
    enabled: open,
  });

  // Resuelve ids→proyecto respetando el orden de favoritos; ignora ids ya sin
  // proyecto visible (borrado/sin acceso).
  const favs = (prefs?.favorites ?? [])
    .map((id) => (projects ?? []).find((p) => p.id === id))
    .filter((p): p is Project => Boolean(p));

  const go = (pid: string) => {
    setOpen(false);
    navigate(`/p/${pid}`);
  };

  return (
    <div className="bd-slot" style={{ '--i': index } as CSSProperties}>
      <button
        type="button"
        className={open ? 'bee-dock-hex active' : 'bee-dock-hex'}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Favoritos"
      >
        <span className="bd-ico">{ICONS.favoritos}</span>
      </button>

      {open && (
        <>
          <button
            type="button"
            className="bd-overlay"
            aria-label="Cerrar favoritos"
            onClick={() => setOpen(false)}
          />
          <div className="dock-pop pop" role="dialog" aria-label="Proyectos favoritos">
            <div className="dock-pop-head">Favoritos</div>
            <div className="dock-pop-body">
              {favs.length === 0 ? (
                <p className="dock-pop-empty muted">
                  Marca proyectos con la estrella para tenerlos a un clic aquí.
                </p>
              ) : (
                favs.map((p) => (
                  <button key={p.id} type="button" className="bd-fav" onClick={() => go(p.id)}>
                    <span className="bd-fav-dot" style={{ background: p.color || 'var(--accent)' }} aria-hidden="true" />
                    <span className="bd-fav-name">{p.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Panorama vive detrás de permiso (catálogo del server, módulo PANORAMA).
const PERM_PANORAMA: PermKey = 'PANORAMA.PANORAMA.VER';

export function BeeDock({ isAdmin = false }: { isAdmin?: boolean }) {
  const { can } = useAuth();
  // Admin por rol; panorama por permiso: si no lo tienes, el hexágono ni existe
  // (la ruta además se protege sola, esto solo evita ofrecer lo que no puedes ver).
  const sections = SECTIONS.filter(
    (it) =>
      (it.key !== 'admin' || isAdmin) && (it.key !== 'panorama' || can(PERM_PANORAMA)),
  );
  const location = useLocation();
  const navRef = useRef<HTMLElement>(null);

  // En táctil no hay hover: el primer tap despliega y un tap fuera repliega. En
  // ratón el despliegue lo maneja el CSS (:hover), así que no interceptamos el clic.
  const [touchOpen, setTouchOpen] = useState(false);
  const canHover = useRef(false);
  useEffect(() => {
    canHover.current = typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;
  }, []);
  useEffect(() => {
    if (!touchOpen) return;
    const onDown = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setTouchOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [touchOpen]);

  // Al NAVEGAR a otra sección, soltar el foco si quedó dentro del dock. Tras hacer
  // clic en un hexágono el NavLink retiene el foco, y la regla :focus-within
  // (chrome.css) mantenía el dock DESPLEGADO hasta que clicabas fuera —aunque el
  // cursor ya hubiera salido—. Con el blur, el :hover vuelve a ser el único que lo
  // sostiene: si el cursor sigue encima permanece abierto, y en cuanto sale (o si
  // navegaste por teclado) se repliega. En táctil, además, se cierra al navegar.
  useEffect(() => {
    const activo = document.activeElement;
    if (navRef.current && activo instanceof HTMLElement && navRef.current.contains(activo)) {
      activo.blur();
    }
    setTouchOpen(false);
  }, [location.pathname]);

  // Hexágono "lead": el de la sección activa por ruta (queda visible en reposo).
  // Si ninguna sección coincide (p. ej. dentro de un proyecto), cae en inicio.
  const leadKey = useMemo(() => {
    const items = [HOME, ...sections];
    const hit = items.find((it) =>
      it.end ? location.pathname === it.to : location.pathname.startsWith(it.to),
    );
    return hit?.key ?? 'home';
  }, [sections, location.pathname]);

  // Táctil replegado → primer tap despliega SIN navegar; ya desplegado, deja pasar.
  const onClickCapture = (e: React.MouseEvent) => {
    if (canHover.current || touchOpen) return;
    e.preventDefault();
    e.stopPropagation();
    setTouchOpen(true);
  };

  return (
    <nav
      ref={navRef}
      className={touchOpen ? 'bee-dock is-touch-open' : 'bee-dock'}
      aria-label="Navegación principal"
      onClickCapture={onClickCapture}
    >
      <DockLink item={HOME} index={0} lead={leadKey === 'home'} />
      {/* Campana anclada al dock: su popover abre HACIA LA DERECHA (anchor="dock"). */}
      <NotifBell anchor="dock" />
      <DockFavorites index={2} />
      {sections.map((it, i) => (
        <DockLink key={it.key} item={it} index={i + 3} lead={leadKey === it.key} />
      ))}
    </nav>
  );
}
